import { EntityTypes } from 'constants/entities';
import { RootState } from 'types';
import { IActionDAO, IActionNode } from 'types/domain/action';
import { IEntityReducer } from 'core/reducers/entities';
import { getEntities } from 'core/selectors/utils/getEntities';
import { CourseNodeType, ICourse, ICourseNodeDAO } from 'types/domain/course';
import { ICourseOut } from 'types/domain/course';
import { Edge, Node } from 'reactflow';
import { maxBy, minBy } from 'lodash';
import { selectOfflineId } from './offlineId';
import { reduceArrayById } from 'utils/reduceArrayById';
import { IKeyArray } from 'types/IKeyArray';
import { Handles } from 'constants/handles';
import { parseActionDAO } from '../product/action/action';
import { parseCourseNodeDAO } from './courseNode';
import { IModuleOut } from 'types/domain/module';
import { parseActionPlaceholders } from '../product/action/actionPlaceholders';
import { createCachedSelector } from 're-reselect';

export interface IEdge {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  data: { id: number; type: EntityTypes; delay: number };
  type: 'buttonedge';
}

const moduleId = (state: RootState, id?) => id || state.state[EntityTypes.MODULE];
const courseId = (state: RootState, id?) => id || state.state[EntityTypes.COURSE];
const selectAll = (state: RootState, id?, all?) => all;
const entity = (state: RootState) => state.entity.present;

const offlineId = selectOfflineId;

const height = 300;
const width = 450;
const widthWithNode = 650;
const modAutoPositions = {};

export const selectModuleNodesAndEdges = createCachedSelector(
  entity,
  moduleId,
  offlineId,
  (entity: IEntityReducer, id: number, offlineId) => {
    if (!id) return { nodes: [], edges: [] };

    const actionsById: IKeyArray<IActionNode> = reduceArrayById(
      getEntities(entity, EntityTypes.ACTION, 'moduleId', id).map((action: IActionDAO, index) =>
        parseActionDAO(entity, action.id),
      ),
    );

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    let index: number = offlineId;

    function convertNode(id, index) {
      if (!actionsById[id]) return null;
      if (actionsById[id].childId && !edges.find(e => e.id === id.toString()))
        edges.push(convertEdge(id, id, actionsById[id].childId));

      return {
        id: `${id}`,
        data: {
          id,
          childId: actionsById[id].childId,
          index,
          nodeId: actionsById[id].nodeId,
          origin:
            actionsById[id].nodeId && entity[EntityTypes.NODE][actionsById[id].nodeId]
              ? entity[EntityTypes.NODE][actionsById[id].nodeId]
              : actionsById[id].node || { x: 0, y: 0 },
        },
        type: EntityTypes.ACTION,
        position:
          actionsById[id].nodeId && entity[EntityTypes.NODE][actionsById[id].nodeId]
            ? entity[EntityTypes.NODE][actionsById[id].nodeId]
            : actionsById[id].node || { x: 0, y: 0 },
      } as Node;
    }
    function convertEdge(id, sourceId, targetId, sourceHandle?, attribs?) {
      return {
        id: id.toString(),
        source: `${sourceId}`,
        sourceHandle: sourceHandle ? `${sourceHandle}` : `${Handles.DEFAULT}`,
        target: `${targetId}`,
        targetHandle: `${Handles.DEFAULT}`,
        data: { id, sourceId, type: EntityTypes.MODULE_OUT, ...(attribs || {}) },
        type: 'buttonedge',
      } as Edge;
    }

    function addChildren(node: Node, depth) {
      //if (courseNodesById[node.data.id].type !== CourseNodeType.AUTO)

      if (!node || nodes.find(n => n.id === node.id)) return;

      nodes.push(node);
      let hasPlaceholders = false;

      node.data.childrenLength =
        Object.values(actionsById[node.data.id].outs).length + actionsById[node.data.id].childId ? 1 : 0;
      node.data.children = [];

      const childId = actionsById[node.data.id]?.outs[Handles.DEFAULT]?.targetId || node.data.childId;

      if (
        childId &&
        childId > 0 &&
        actionsById[childId] &&
        actionsById[childId].status > -1 &&
        childId !== node.data.id &&
        !nodes.find(n => n.id === childId.toString())
      ) {
        const child = convertNode(childId, depth + 1);
        if (!child) return;
        child.data.parentActionId = node.data.parentActionId ? node.data.id : null;

        if (!child.data.nodeId)
          child.position = child.data.origin = {
            x: node.position.x + (hasPlaceholders ? widthWithNode : width),
            y: node.position.y,
          };
        modAutoPositions[child.id] = child.position;
        if (child.data.parentActionId) node.data.children.push(child);
        addChildren(child, depth + 1);
      }

      parseActionPlaceholders(actionsById[node.data.id], node.position, index)
        .reverse()
        .map((ph, i) => {
          index--;
          hasPlaceholders = true;
          nodes.push(ph);
          node.data.children.push(ph);
          if (!edges.find(e => e.id === ph.id.toString()))
            edges.push(
              convertEdge(ph.id, node.data.id, ph.id, ph.data.sourceHandle, {
                ...ph.data,
                parentNodeId: node.data.id,
                placeholder: true,
              }),
            );
        });

      Object.values(actionsById[node.data.id].outs)
        .filter(
          (out: IModuleOut) =>
            out.targetId &&
            out.sourceHandle !== Handles.DEFAULT &&
            actionsById[out.targetId] &&
            !nodes.find(n => n.id === out.targetId.toString()),
        )
        .forEach((out: IModuleOut, i) => {
          const child = convertNode(out.targetId, null);
          if (!child) return;

          //child.parentNode = node.id;
          child.data.parentEntityId = out.id;
          child.data.parentActionId = node.data.id;
          child.data.parentHandle = out.sourceHandle;
          if (!child.data.nodeId) {
            child.position = child.data.origin = {
              x: node.position.x + (out.sourceHandle !== Handles.FAIL && hasPlaceholders ? widthWithNode : width),
              y: out.sourceHandle === Handles.FAIL ? node.position.y - height : node.position.y + height * (i + 1),
            };
          }
          node.data.children.push(child);
          addChildren(child, depth + 1);
        });
    }

    const ophans = Object.values(actionsById).filter(
      a =>
        !Object.values(actionsById).find(
          (ab: IActionDAO) =>
            ab.childId === a.id || Object.values(ab.outs).find((o: IModuleOut) => o.targetId === a.id),
        ),
    );

    const firstNode =
      actionsById[entity[EntityTypes.MODULE][id]?.actionId] ||
      minBy(
        ophans.filter((a: IActionDAO) => a.node),
        (a: any) => a.node.x,
      ) ||
      minBy(ophans, 'id');

    if (firstNode) addChildren(convertNode(firstNode.id, 1), 1);

    getEntities(entity, EntityTypes.MODULE_OUT, 'moduleId', id)
      .filter(mo => !!mo)
      .filter(mo => entity[EntityTypes.ACTION][mo.sourceId] && entity[EntityTypes.ACTION][mo.targetId])
      .forEach((mo: IModuleOut) => {
        if (!edges.find(e => e.id === mo.id.toString()))
          edges.push({
            id: mo.id.toString(),
            source: `${mo.sourceId}`,
            sourceHandle: `${mo.sourceHandle}`,
            target: `${mo.targetId}`,
            targetHandle: `${mo.targetHandle}`,
            data: { id: mo.id, type: EntityTypes.MODULE_OUT, sourceHandle: mo.sourceHandle },
            type: 'buttonedge',
          });
      });

    const lastNode = maxBy(nodes, n => n.position.x);

    Object.values(actionsById)
      .filter(cn => !nodes.find(n => n.data.id === cn.id) && cn.id > 0)
      .forEach((cn, i) => {
        const node = convertNode(cn.id, nodes.length + i);
        if (!node.data.nodeId)
          node.position = modAutoPositions[node.id] || {
            x: (lastNode?.position.x || 0) + widthWithNode,
            y: (lastNode?.position.y || 0) + height,
          };
        addChildren(node, nodes.length + i);
      });

    return {
      nodes,
      edges,
    };
  },
)((state, id) => id || state.state[EntityTypes.MODULE] || 'unknown');

const courseAutoPositions = {};

export const selectCourseNodesAndEdges = createCachedSelector(
  entity,
  courseId,
  selectAll,
  (entity: IEntityReducer, courseId: number, selectAll: boolean) =>
    parseCourseNodesAndEdges(entity, courseId, selectAll),
)((state, id) => id || state.state[EntityTypes.COURSE] || 'unknown');

export function parseCourseNodesAndEdges(entity: IEntityReducer, id: number, selectAll?: boolean) {
  const course: ICourse = entity[EntityTypes.COURSE][id];

  if (!course) return { nodes: [], edges: [] };

  const courseNodesById: IKeyArray<ICourseNodeDAO> = reduceArrayById(
    getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', id).map(n => parseCourseNodeDAO(entity, n.id)),
  );

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  function convertNode(id) {
    if (!courseNodesById[id]) return null;

    if (courseNodesById[id].childId) convertEdge(id, id, courseNodesById[id].childId, id);

    const position = courseNodesById[id].nodeId
      ? entity[EntityTypes.NODE][courseNodesById[id].nodeId]
      : courseNodesById[id].node
      ? courseNodesById[id].node
      : { x: 0, y: 0 };

    return {
      id: `${id}`,
      data: {
        id,
        childId: courseNodesById[id].childId,
        nodeId: courseNodesById[id].nodeId,
        moduleId: courseNodesById[id].moduleId,
        origin: position,
      },
      children: [],
      type: EntityTypes.COURSE_NODE,
      position,
    } as Node;
  }
  function convertEdge(id, sourceId, targetId, parentNodeId) {
    if (!edges.find(e => e.id === `${id}.${sourceId}.${targetId}`))
      edges.push({
        id: `${id}.${sourceId}.${targetId}`,
        source: `${sourceId}`,
        sourceHandle: `${Handles.DEFAULT}`,
        target: `${targetId}`,
        targetHandle: `${Handles.DEFAULT}`,
        data: { id: id, type: EntityTypes.COURSE_OUT, parentNodeId },
        type: 'buttonedge',
      } as Edge);
  }

  function addChildren(node: Node, depth) {
    //if (courseNodesById[node.data.id].type !== CourseNodeType.AUTO)

    if (!node || nodes.find(n => n.id === node.id)) return;

    nodes.push(node);

    courseNodesById[node.data.id]?.outs
      .reduce(
        (prev, next) => (next.sourceHandle === Handles.DEFAULT ? [...prev, next.targetId] : prev),
        node.data.childId ? [node.data.childId] : [],
      )
      .filter(id => !!courseNodesById[id] && courseNodesById[id].status > -1)
      .forEach(id => {
        const child = convertNode(id);

        if (!child) return;

        if (!child.data.nodeId || !node.position) {
          child.position = child.data.origin = {
            x: (node.position?.x || 0) + width,
            y: node.position?.y || 0,
          };
          courseAutoPositions[child.id] = child.position;
        }

        addChildren(child, depth + 1);
      });
  }

  if (course.courseNodeId) addChildren(convertNode(course.courseNodeId), 0);

  getEntities(entity, EntityTypes.COURSE_OUT, 'courseId', id)
    .filter(mo => !!mo)
    .filter(mo => entity[EntityTypes.COURSE_NODE][mo.sourceId] && entity[EntityTypes.COURSE_NODE][mo.targetId])
    .map((mo: ICourseOut) => {
      edges.push({
        id: mo.id.toString(),
        source: `${mo.sourceId}`,
        sourceHandle: `${mo.sourceHandle}`,
        target: `${mo.targetId}`,
        targetHandle: `${mo.targetHandle}`,
        data: { id: mo.id, type: EntityTypes.COURSE_OUT, delay: mo.delay },
        type: 'buttonedge',
      });
    });

  const lastNode = maxBy(nodes, n => n.position?.x) || nodes[nodes.length - 1];

  Object.values(courseNodesById)
    .filter(cn => !nodes.find(n => n.data.id === cn.id) && (selectAll || cn.nodeId || courseAutoPositions[cn.id]))
    .forEach((cn, i) => {
      const node = convertNode(cn.id);
      if (!node.data.nodeId || !node.position) {
        node.position = node.data.origin = courseAutoPositions[cn.id] || {
          x: (lastNode?.position.x || 0) + widthWithNode,
          y: (lastNode?.position.y || 0) + height,
        };
      }

      addChildren(node, nodes.length + i);
    });

  return {
    nodes,
    edges,
  };
}
